import React from 'react';
import { Card, Typography, Tag, Tooltip, Row, Col, Divider, Statistic } from 'antd';
import { 
  BulbOutlined, 
  ArrowUpOutlined, 
  InfoCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;

/**
 * OTRInsightCard component for displaying AI-powered insights
 * @param {Object} props Component props
 * @param {String} props.title Title of the insight
 * @param {String} props.insight The insight text
 * @param {String} props.recommendation Recommended action
 * @param {Number} props.improvement Estimated OTR improvement percentage
 * @param {String} props.category Category of the insight (trip, stop, driver)
 */
const OTRInsightCard = ({ title, insight, recommendation, improvement, category }) => {
  if (!insight && !recommendation) {
    return (
      <Card style={{ width: 400, padding: 0 }}>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <InfoCircleOutlined style={{ fontSize: 24, color: '#999' }} />
          <Paragraph style={{ marginTop: 8 }}>No insights available</Paragraph>
        </div>
      </Card>
    );
  }
  
  const getCategoryColor = () => {
    switch(category) {
      case 'trip': return 'blue';
      case 'stop': return 'purple';
      case 'driver': return 'orange';
      default: return 'default';
    }
  };
  
  const getCategoryLabel = () => {
    switch(category) {
      case 'trip': return 'Trip';
      case 'stop': return 'Stop';
      case 'driver': return 'Driver';
      default: return 'General';
    }
  };

  const getTitleTooltip = () => {
    switch(category) {
      case 'trip': return 'AI-generated insights about trip performance and potential improvements';
      case 'stop': return 'AI-generated insights about stop performance and potential improvements';
      case 'driver': return 'AI-generated insights about driver performance and potential improvements';
      default: return 'AI-generated insights and recommendations for improvement';
    }
  };
  
  return (
    <Card 
      style={{ width: 400, padding: 0 }}
      title={
        <Tooltip title={getTitleTooltip()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BulbOutlined style={{ color: '#faad14', marginRight: 8 }} />
              <span>{title || 'AI Insight'}</span>
            </div>
            {category && (
              <Tag color={getCategoryColor()}>
                {getCategoryLabel()}
              </Tag>
            )}
          </div>
        </Tooltip>
      }
    >
      <div style={{ padding: '0 8px' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <InfoCircleOutlined style={{ marginRight: 8, marginTop: 4 }} />
              <div>
                <Text strong>Insight:</Text>
                <Paragraph style={{ marginTop: 4 }}>{insight}</Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <WarningOutlined style={{ marginRight: 8, marginTop: 4 }} />
              <div>
                <Text strong>Recommendation:</Text>
                <Paragraph style={{ marginTop: 4 }}>{recommendation}</Paragraph>
              </div>
            </div>
          </Col>
          
          {improvement > 0 && (
            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
              <Row justify="space-between" align="middle">
                <Col>
                  <Text>Estimated OTR Improvement:</Text>
                </Col>
                <Col>
                  <Statistic 
                    value={improvement} 
                    suffix="%" 
                    valueStyle={{ 
                      color: improvement >= 20 ? '#52c41a' : 
                             improvement >= 10 ? '#faad14' : '#1890ff' 
                    }}
                    prefix={<ArrowUpOutlined />}
                  />
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </div>
    </Card>
  );
};

export default OTRInsightCard; 